/**
 * userProfileService.ts (Final Version)
 *
 * This service is responsible for saving and loading the user's life profile
 * to and from the browser's local storage. It has been updated to use the
 * definitive, unified `UserProfile` type.
 */
import { UserProfile } from '@/types'; // Imports from your unified types/index.ts

// A more generic and clear key for storing the profile data.
const PROFILE_KEY = 'lotteryOfLife_userProfile';

export function loadProfileSafe(): any | null {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (err) {
    console.warn('[Profile] Corrupt JSON, clearing.', err);
    localStorage.removeItem(PROFILE_KEY);
    return null;
  }
}

export function createSkeletonProfile(user: { uid: string; email?: string | null }) {
  return {
    uid: user.uid,
    email: user.email ?? null,
    displayName: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    preferences: {},
    lastActiveColonyId: null,
    goals: [],
  };
}

export function saveProfileSafe(profile: any) {
  try {
    const clean = { ...profile, updatedAt: new Date().toISOString() };
    localStorage.setItem(PROFILE_KEY, JSON.stringify(clean));
    return clean;
  } catch (err) {
    console.error('[Profile] Failed to save profile.', err);
    return profile;
  }
}

export const userProfileService = {
  /**
   * Creates a new, empty "skeleton" profile for a new user.
   * This ensures that the profile object and its keys always exist, even if the values are null.
   * @param uid The user's unique ID from Firebase.
   * @param email The user's email from Firebase.
   * @returns A complete UserProfile object with default values.
   */
  // Legacy typed helper retained for older call sites
  createSkeletonProfile(uid: string, email: string | null): UserProfile {
    return {
      uid,
      email: email || '',
      createdAt: Date.now(),
    };
  },

  /**
   * Loads the user's profile from local storage.
   * @returns A promise that resolves to the UserProfile object or null if not found.
   */
  async loadProfile(): Promise<UserProfile | null> {
    try {
      const data = localStorage.getItem(PROFILE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error("Failed to load profile from local storage:", error);
      return null;
    }
  },

  /**
   * Saves the user's profile to local storage.
   * @param profile The complete UserProfile object to save.
   */
  async saveProfile(profile: UserProfile): Promise<void> {
    try {
      localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
    } catch (error) {
      console.error("Failed to save profile to local storage:", error);
    }
  },
};

export const saveUserProfile = userProfileService.saveProfile;
